import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ChevronDown } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";
import { cn } from "@/lib/utils";

interface BuyNowConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

export const BuyNowConfirmDialog = ({
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
}: BuyNowConfirmDialogProps) => {
  const { language } = useLanguage();
  const bi = carDetailsLabels(language).biddingInfo;
  const how = carDetailsLabels(language).howBuyingWorks;
  const [howOpen, setHowOpen] = useState(false);

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setHowOpen(false);
        onOpenChange(next);
      }}
    >
      <AlertDialogContent className="max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle>{bi.confirmPurchase}</AlertDialogTitle>
          <AlertDialogDescription className="text-base">
            {bi.confirmPurchaseDesc}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <section className="overflow-hidden rounded-2xl border border-border/70 bg-gradient-to-br from-sky-500/[0.06] via-background to-emerald-500/[0.05]">
          <button
            type="button"
            onClick={() => setHowOpen((v) => !v)}
            aria-expanded={howOpen}
            className="flex w-full items-center gap-3 px-3.5 py-3 text-left transition-colors hover:bg-black/[0.02] dark:hover:bg-white/[0.03]"
          >
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold tracking-tight text-foreground">
                {how.title}
              </span>
              {!howOpen ? (
                <span className="mt-0.5 block text-[12px] text-muted-foreground line-clamp-1">
                  {how.teaser}
                </span>
              ) : null}
            </span>
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-300",
                howOpen && "rotate-180",
              )}
            />
          </button>
          <div
            className={cn(
              "grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]",
              howOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
            )}
          >
            <div className="min-h-0 overflow-hidden">
              <div className="space-y-2.5 border-t border-border/60 px-3.5 pb-3.5 pt-2.5">
                {(Array.isArray(how.body) ? how.body : [how.body]).map(
                  (para, i) => (
                    <p
                      key={i}
                      className="text-[12.5px] leading-relaxed text-foreground/90"
                    >
                      {para}
                    </p>
                  ),
                )}
                <p className="rounded-xl border border-amber-500/20 bg-amber-500/[0.08] px-3 py-2 text-[12px] leading-relaxed text-foreground/85">
                  {how.privateNote}
                </p>
              </div>
            </div>
          </div>
        </section>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel} disabled={isLoading}>
            {bi.no}
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? bi.reserving : bi.yes}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
